import { useEffect, useState } from 'react';
import { DocumentTranscriptUtteranceV1 } from '@utils/types/editors/transcripts/transcript.ts';
import cn from 'classnames';
import ScriptixTranscriptEditor from '@components/editors/transcript-editor/scriptix-transcript-editor.tsx';
import { useThemeContext } from '@utils/providers/theme-context.tsx';

type Props = {
  index: number;
  paragraph: DocumentTranscriptUtteranceV1;
  onWordOutOfView: (
    ref: HTMLSpanElement | null,
    direction: 'up' | 'down'
  ) => void;
  onWordInView: () => void;
  readOnly?: boolean;
};

export const TranscriptRow = ({
  index,
  paragraph,
  onWordOutOfView,
  onWordInView,
  readOnly
}: Props) => {
  const [isActive, setIsActive] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { theme } = useThemeContext();

  // Theme-aware colors
  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const activeBorderColor = pick('rgb(191 219 254)', 'rgb(59 130 246)'); // blue-200 / blue-500
  const activeBgColor = pick('rgb(239 246 255)', 'rgba(30, 58, 138, 0.2)'); // blue-50 / blue-900/20
  const hoverBorderColor = pick('rgb(229 231 235)', 'rgb(55 65 81)'); // gray-200 / gray-700
  const hoverBgColor = pick('rgb(249 250 251)', 'rgba(31, 41, 55, 0.3)'); // gray-50 / gray-800/30
  const restBgColor = pick('rgb(255 255 255)', 'rgb(31 41 55)'); // white / gray-800
  const restBorderColor = pick('rgb(229 231 235)', 'rgb(55 65 81)'); // gray-200 / gray-700

  useEffect(() => {
    const handleActiveRow = (e: Event) => {
      const { index: activeIndex } = (e as CustomEvent).detail || {};
      setIsActive(activeIndex === index);
    };

    window.addEventListener(
      'transcript:activeRow',
      handleActiveRow as EventListener
    );
    return () => {
      window.removeEventListener(
        'transcript:activeRow',
        handleActiveRow as EventListener
      );
    };
  }, [index]);

  const handleRowClick = () => {
    window.dispatchEvent(
      new CustomEvent('transcript:activeRow', { detail: { index } })
    );
  };

  const getStyles = () => {
    const textColor = pick('rgb(0 0 0)', 'rgb(255 255 255)'); // black / white

    if (isActive) {
      return {
        borderColor: activeBorderColor,
        backgroundColor: activeBgColor,
        color: textColor
      };
    }
    if (isHovered) {
      return {
        borderColor: hoverBorderColor,
        backgroundColor: hoverBgColor,
        color: textColor
      };
    }
    return {
      borderColor: restBorderColor,
      backgroundColor: restBgColor,
      color: textColor
    };
  };

  if (readOnly) {
    // Split-view source pane (#2237): visual-only for editing, but
    // text selection + copy must still work. Slate's `readOnly` on
    // the inner editor blocks input and hides the caret; the wrapper
    // keeps `select-text` + `cursor-text` so the user can select
    // phrases to copy. Hover affordances are suppressed so the row
    // doesn't suggest it's clickable.
    return (
      <div
        className="rounded-2xl border p-2 select-text"
        style={{
          borderColor: restBorderColor,
          backgroundColor: restBgColor,
          color: pick('rgb(0 0 0)', 'rgb(255 255 255)'),
          cursor: 'text'
        }}
        aria-readonly="true"
      >
        <ScriptixTranscriptEditor
          index={index}
          paragraph={paragraph}
          onWordOutOfView={onWordOutOfView}
          onWordInView={onWordInView}
          readOnly
        />
      </div>
    );
  }

  return (
    <div
      // Marker for the comments gutter (#2244): anchor cards snap
      // their bottom-right corner to this row's bottom-right corner.
      data-paragraph-row=""
      onClick={handleRowClick}
      onFocus={handleRowClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'rounded-2xl transition-all duration-200 cursor-pointer',
        'border p-2',
        isActive && 'shadow-md'
      )}
      style={getStyles()}
    >
      <ScriptixTranscriptEditor
        index={index}
        paragraph={paragraph}
        onWordOutOfView={onWordOutOfView}
        onWordInView={onWordInView}
      />
    </div>
  );
};
